import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService, type AccessLevel } from '../services/AuthService';
import { persistentLogger } from '../utils/PersistentLogger';

interface AuthContextType {
  isAuthenticated: boolean;
  userName: string | null;
  allowedFolders: string[] | null;
  accessLevel: AccessLevel | null;
  login: (token: string, name: string, allowedFolders?: string[], accessLevel?: AccessLevel) => void;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
  hasAccessToFolder: (folder: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const auth = AuthService.isAuthenticated();
    persistentLogger.log('AuthContext', `Initializing isAuthenticated state: ${auth}`, { 
      hasToken: !!AuthService.getToken(),
      userName: AuthService.getUserName()
    });
    return auth;
  });
  const [userName, setUserName] = useState<string | null>(() => AuthService.getUserName());
  const [allowedFolders, setAllowedFolders] = useState<string[] | null>(() => AuthService.getAllowedFolders());
  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>(() => AuthService.getAccessLevel());

  const login = (token: string, name: string, folders?: string[], level?: AccessLevel) => {
    persistentLogger.log('AuthContext', 'login() called', { name, hasToken: !!token, allowedFolders: folders, accessLevel: level });
    AuthService.setAuth(token, name, folders, level);
    setIsAuthenticated(true);
    setUserName(name);
    setAllowedFolders(folders || null);
    setAccessLevel(level || null);
    persistentLogger.log('AuthContext', 'login() completed - isAuthenticated set to true', {
      isAuthenticated: true,
      userName: name,
      allowedFolders: folders,
      accessLevel: level,
    });
  };

  const logout = () => {
    persistentLogger.warn('AuthContext', 'logout() called - clearing auth');
    AuthService.clearAuth();
    setIsAuthenticated(false);
    setUserName(null);
    setAllowedFolders(null);
    setAccessLevel(null);
    persistentLogger.log('AuthContext', 'logout() completed - isAuthenticated set to false');
  };

  const hasAccessToFolder = (folder: string): boolean => {
    // Default to full access if no folders stored (legacy tokens)
    if (!allowedFolders) return true;
    return allowedFolders.includes(folder);
  };

  const verifyToken = async (): Promise<boolean> => {
    persistentLogger.log('AuthContext', 'verifyToken() called');
    const token = AuthService.getToken();
    if (!token) {
      persistentLogger.warn('AuthContext', 'verifyToken() - No token found');
      return false;
    }

    try {
      persistentLogger.log('AuthContext', 'verifyToken() - Sending verification request');
      const authHeader = AuthService.getAuthHeader();
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': authHeader!,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserName(data.name);
        setAllowedFolders(data.allowedFolders ?? null);
        setAccessLevel(data.accessLevel ?? null);
        AuthService.setAuth(token, data.name, data.allowedFolders, data.accessLevel);
        persistentLogger.log('AuthContext', 'verifyToken() - Token verified successfully', {
          userName: data.name,
          allowedFolders: data.allowedFolders,
          accessLevel: data.accessLevel,
        });
        return true;
      } else {
        // Token is invalid, clear auth
        persistentLogger.error('AuthContext', 'verifyToken() - Token verification failed', { 
          status: response.status,
          statusText: response.statusText
        });
        logout();
        return false;
      }
    } catch (error) {
      persistentLogger.error('AuthContext', 'verifyToken() - Exception during verification', { error });
      logout();
      return false;
    }
  };

  // When auth is cleared elsewhere (e.g. 401 in api.ts), sync state so UI shows Login without reload
  useEffect(() => {
    const handleAuthCleared = () => {
      persistentLogger.log('AuthContext', 'auth-cleared event - syncing state to logged out');
      setIsAuthenticated(false);
      setUserName(null);
      setAllowedFolders(null);
      setAccessLevel(null);
    };
    window.addEventListener(AuthService.AUTH_CLEARED_EVENT, handleAuthCleared);
    return () => window.removeEventListener(AuthService.AUTH_CLEARED_EVENT, handleAuthCleared);
  }, []);

  // Verify token on mount - but only if we didn't just log in
  // AppInitializer handles the main verification on initial load
  // This is just for keeping state in sync on page refresh
  useEffect(() => {
    persistentLogger.log('AuthContext', 'useEffect on mount', { isAuthenticated });
    if (isAuthenticated) {
      persistentLogger.log('AuthContext', 'Scheduling token verification in 500ms');
      // Small delay to avoid race condition with AppInitializer
      const timer = setTimeout(() => {
        persistentLogger.log('AuthContext', 'Running scheduled token verification');
        verifyToken();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      persistentLogger.log('AuthContext', 'Not authenticated, skipping token verification');
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, userName, allowedFolders, accessLevel, login, logout, verifyToken, hasAccessToFolder }}>
      {children}
    </AuthContext.Provider>
  );
};

